import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  connectAuthEmulator,
  GoogleAuthProvider,
  OAuthProvider,
  type Auth,
} from 'firebase/auth';
import {
  initializeFirestore,
  connectFirestoreEmulator,
  persistentLocalCache,
  persistentMultipleTabManager,
  clearIndexedDbPersistence,
  type Firestore,
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from 'firebase/app-check';
import { readEnv, isDev } from './env.js';
import { isNative } from './device.js';

/**
 * Single Firebase entry point. App Check is initialized before any other SDK
 * call so callables/Storage are never hit unattested (LEGAL §6).
 * Region is pinned to us-central1 and documented (LEGAL §6 data residency).
 */
export const FUNCTIONS_REGION = 'us-central1';

interface FirebaseBundle {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  storage: FirebaseStorage;
  functions: Functions;
  appCheck: AppCheck | null;
}

let bundle: FirebaseBundle | null = null;

/**
 * Custom domains on this project's Hosting site. Each serves the auth handler
 * (/__/auth/handler) too, so sign-in there uses the page's own host as
 * authDomain and the OAuth round trip stays same-origin (stores/auth.ts,
 * prefersRedirect). Adding one also needs, outside the code: the domain in
 * Auth › Settings › Authorized domains, and https://<domain>/__/auth/handler
 * in the Google OAuth web client's redirect URIs.
 */
const HOSTED_AUTH_DOMAINS = ['erectus.app'];

function authDomainFor(configured: string): string {
  const host = window.location.hostname;
  return !isNative() && HOSTED_AUTH_DOMAINS.includes(host) ? host : configured;
}

declare global {
  // eslint-disable-next-line no-var
  var FIREBASE_APPCHECK_DEBUG_TOKEN: string | boolean | undefined;
}

export function firebase(): FirebaseBundle {
  if (bundle) return bundle;
  const env = readEnv();

  const app = initializeApp({
    apiKey: env.apiKey,
    authDomain: authDomainFor(env.authDomain),
    projectId: env.projectId,
    storageBucket: env.storageBucket,
    messagingSenderId: env.messagingSenderId,
    appId: env.appId,
    ...(env.measurementId ? { measurementId: env.measurementId } : {}),
  });

  let appCheck: AppCheck | null = null;
  if (isDev && env.appCheckDebugToken) {
    globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN = env.appCheckDebugToken;
  }
  if (env.recaptchaSiteKey) {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(env.recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } else if (!isDev && !env.useEmulators && !env.allowNoAppCheck) {
    // Fail loudly for a real deployment: unattested traffic is a policy
    // violation, not a warning (docs/LEGAL_PRIVACY.md §6). Emulator and dev
    // builds are exempt, and a deliberate pre-launch build can opt out with
    // VITE_ALLOW_NO_APP_CHECK=1 — which must be paired with
    // ENFORCE_APP_CHECK=false on the functions deploy, or every callable will
    // reject the app's own requests.
    throw new Error(
      'VITE_RECAPTCHA_ENTERPRISE_SITE_KEY is required for a production build. ' +
        'Set it, or set VITE_ALLOW_NO_APP_CHECK=1 to build without attestation.',
    );
  }

  /**
   * Native: no popup/redirect resolver. getAuth() wires one in, and it loads
   * an iframe from authDomain that never resolves inside a Capacitor WebView,
   * stalling auth state. Sign-in there goes through the native plugin and
   * signInWithCredential (stores/auth.ts), which needs no resolver.
   */
  const auth = isNative()
    ? initializeAuth(app, { persistence: indexedDBLocalPersistence })
    : getAuth(app);
  /**
   * Persistent local cache: this is a phone app that gets opened on a train.
   * Session summaries the user already fetched stay readable with no network.
   *
   * It is a second place their health data lives on the device, so account
   * deletion must clear it too — see `clearFirestoreCache()`, called from the
   * deletion flow alongside the Dexie wipe.
   */
  const db = initializeFirestore(app, {
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  });
  const storage = getStorage(app);
  // Reads (replay files) give up after 20 s instead of the SDK's 2 min, so a
  // blocked download shows its error while the user is still looking. Uploads
  // keep their own, longer retry window (maxUploadRetryTime).
  storage.maxOperationRetryTime = 20_000;
  const functions = getFunctions(app, FUNCTIONS_REGION);

  if (env.useEmulators) {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    connectStorageEmulator(storage, '127.0.0.1', 9199);
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  }

  bundle = { app, auth, db, storage, functions, appCheck };
  return bundle;
}

/**
 * Wipe Firestore's on-device cache. Part of account deletion: the server data is
 * gone, and this is the local copy of it.
 */
export async function clearFirestoreCache(): Promise<void> {
  if (!bundle) return;
  try {
    await clearIndexedDbPersistence(bundle.db);
  } catch {
    // Throws while the SDK is still active; the account is signed out straight
    // after, and the cache is dropped with the browser storage on next boot.
  }
}

export const googleProvider = new GoogleAuthProvider();
/** Apple sign-in is mandatory on iOS once any other social provider ships. */
export const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');
